/**
 * Device Dispatch Page - Admin Portal
 * Select client, choose QC-passed devices, confirm provisioning
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { controlApi } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Check, Building2, Cpu, Send, Mail, Monitor, ArrowRight,
  Package, Truck, Eye, Loader2, CheckCircle
} from 'lucide-react';
import { getBearerToken } from '@/context/TrackOticAuthContext';

export default function DeviceDispatchPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  
  // Step 1: Client Selection
  const [selectedClient, setSelectedClient] = useState(null);
  const [clients, setClients] = useState([]);

  // Step 2: Device Selection
  const [availableDevices, setAvailableDevices] = useState([]);
  const [selectedDevices, setSelectedDevices] = useState([]);

  // Step 3: Delivery Note
  const [deliveryNote, setDeliveryNote] = useState('');

  // Dispatch History
  const [dispatchHistory, setDispatchHistory] = useState([]);

  // Fetch clients and devices on mount (wait for auth token)
  useEffect(() => {
    const token = getBearerToken();
    if (!token) return;
    
    async function fetchData() {
      try {
        setLoading(true);
        const [clientsRes, devicesRes, historyRes] = await Promise.all([
          controlApi.getClientsForDispatch(),
          controlApi.getRegistryDevices({}),
          controlApi.getDispatchHistory()
        ]);
        
        setClients(clientsRes.data || []);
        
        // Map devices: ready ones are selectable, others are not
        const devices = (devicesRes.data || []).map(d => {
          const qc = (d.qc_status || '').toLowerCase();
          const fota = (d.fota_status || '').toLowerCase();
          const prov = (d.provisioning_status || '').toLowerCase();
          return {
            id: d.id,
            device_id: d.device_uid,
            type: (d.device_type || '').toUpperCase(),
            qc_status: qc === 'passed' ? 'passed' : fota === 'in_progress' ? 'fota' : 'failed',
            selectable: qc === 'passed' && prov === 'in_stock'
          };
        });
        setAvailableDevices(devices);
        
        // Map dispatch history
        const history = (historyRes.data || []).map(d => {
          const st = (d.status || '').toLowerCase();
          return {
            date: d.dispatched_at ? new Date(d.dispatched_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—',
            client: d.tenant,
            pro: d.pro_count,
            hub: d.hub_count,
            lite: d.lite_count,
            by: d.dispatched_by,
            status: st === 'delivered' ? 'delivered' : st === 'in_transit' ? 'in_transit' : 'pending'
          };
        });
        setDispatchHistory(history);
      } catch (error) {
        toast.error('Failed to load dispatch data');
      } finally {
        setLoading(false);
      }
    }
    fetchData();
     
  }, []);

  const toggleDevice = (deviceId) => {
    const device = availableDevices.find(d => d.id === deviceId);
    if (!device?.selectable) return;

    setSelectedDevices(prev => 
      prev.includes(deviceId) 
        ? prev.filter(id => id !== deviceId)
        : [...prev, deviceId]
    );
  };

  const getSelectedSummary = () => {
    const selected = availableDevices.filter(d => selectedDevices.includes(d.id));
    return {
      pro: selected.filter(d => d.type === 'PRO').length,
      hub: selected.filter(d => d.type === 'HUB').length,
      lite: selected.filter(d => d.type === 'LITE').length,
      total: selected.length
    };
  };

  const handleConfirmDispatch = async () => {
    try {
      setConfirming(true);
      const payload = {
        tenant_id: selectedClient.id,
        device_registry_ids: selectedDevices,
        delivery_note: deliveryNote || null
      };
      const res = await controlApi.dispatchDevices(payload);
      
      toast.success(`${res.data.total_devices} devices dispatched to ${res.data.tenant}`, {
        description: `Dispatch #${res.data.dispatch_number} created`
      });
      
      setConfirmModalOpen(false);
      // Reset form
      setSelectedClient(null);
      setSelectedDevices([]);
      setDeliveryNote('');
      
      // Refresh data
      const [devicesRes, historyRes] = await Promise.all([
        controlApi.getRegistryDevices({}),
        controlApi.getDispatchHistory()
      ]);
      const devices = (devicesRes.data || []).map(d => {
        const qc = (d.qc_status || '').toLowerCase();
        const fota = (d.fota_status || '').toLowerCase();
        const prov = (d.provisioning_status || '').toLowerCase();
        return {
          id: d.id,
          device_id: d.device_uid,
          type: (d.device_type || '').toUpperCase(),
          qc_status: qc === 'passed' ? 'passed' : fota === 'in_progress' ? 'fota' : 'failed',
          selectable: qc === 'passed' && prov === 'in_stock'
        };
      });
      setAvailableDevices(devices);
      const history = (historyRes.data || []).map(d => {
        const st = (d.status || '').toLowerCase();
        return {
          date: d.dispatched_at ? new Date(d.dispatched_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—',
          client: d.tenant,
          pro: d.pro_count,
          hub: d.hub_count,
          lite: d.lite_count,
          by: d.dispatched_by,
          status: st === 'delivered' ? 'delivered' : st === 'in_transit' ? 'in_transit' : 'pending'
        };
      });
      setDispatchHistory(history);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to dispatch devices');
    } finally {
      setConfirming(false);
    }
  };

  const summary = getSelectedSummary();
  const currentStep = !selectedClient ? 1 : selectedDevices.length === 0 ? 2 : 3;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-3 text-slate-500">Loading dispatch data...</span>
      </div>
    );
  }

  const getTypeColor = (type) => {
    switch (type) {
      case 'PRO': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
      case 'HUB': return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400';
      case 'LITE': return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Dispatch Devices to Customer</h1>
          <p className="text-slate-500 dark:text-slate-400">
            Select client, choose QC-passed devices, confirm provisioning
          </p>
        </div>
        <Button 
          className="gap-2 bg-blue-600 hover:bg-blue-700"
          disabled={!selectedClient || selectedDevices.length === 0}
          onClick={() => setConfirmModalOpen(true)}
        >
          <Send className="w-4 h-4" />
          Confirm dispatch
        </Button>
      </div>

      {/* Dispatch Steps */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { num: 1, title: 'Select client', desc: 'Choose tenant account' },
          { num: 2, title: 'Select devices', desc: 'QC-passed only' },
          { num: 3, title: 'Confirm & provision', desc: 'Devices go to client pool' },
          { num: 4, title: 'Client notified', desc: 'Email + portal access' },
        ].map((step) => (
          <div
            key={step.num}
            className={`rounded-lg p-4 border ${
              step.num <= currentStep
                ? 'bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800'
                : 'bg-slate-50 border-slate-200 dark:bg-slate-800/50 dark:border-slate-700'
            }`}
          >
            <div
              className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium mb-2 ${
                step.num <= currentStep
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-300 text-slate-600 dark:bg-slate-600 dark:text-slate-400'
              }`}
            >
              {step.num < currentStep ? <Check className="w-3 h-3" /> : step.num}
            </div>
            <p className={`text-sm font-medium ${
              step.num <= currentStep ? 'text-blue-700 dark:text-blue-400' : 'text-slate-500'
            }`}>
              {step.title}
            </p>
            <p className={`text-xs mt-1 ${
              step.num <= currentStep ? 'text-blue-600/70 dark:text-blue-400/70' : 'text-slate-400'
            }`}>
              {step.desc}
            </p>
          </div>
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-2 gap-6">
        {/* Step 1: Client Selection */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Step 1 — Select client</CardTitle>
              {selectedClient && (
                <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                  Selected
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm text-slate-500 mb-1 block">Client / tenant</label>
              <Select
                value={selectedClient?.id || ''}
                onValueChange={(value) => setSelectedClient(clients.find(c => c.id === value))}
              >
                <SelectTrigger className={selectedClient ? 'border-green-300 bg-green-50 dark:bg-green-900/20 dark:border-green-800' : ''}>
                  <SelectValue placeholder="Select a client..." />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedClient && (
              <>
                <div>
                  <label className="text-sm text-slate-500 mb-1 block">Tenant ID</label>
                  <div className="font-mono text-sm bg-slate-100 dark:bg-slate-800 p-2 rounded">
                    {selectedClient.code || selectedClient.id}
                  </div>
                </div>
                <div>
                  <label className="text-sm text-slate-500 mb-1 block">Account status</label>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                      {selectedClient.status || 'Active'}
                    </Badge>
                    {selectedClient.onboarded_at && (
                      <span className="text-sm text-slate-500">· Onboarded {new Date(selectedClient.onboarded_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                    )}
                  </div>
                </div>
                <div>
                  <label className="text-sm text-slate-500 mb-1 block">Existing devices in pool</label>
                  <div className="text-sm">
                    {selectedClient.existing_devices?.total || 0} devices · PRO ×{selectedClient.existing_devices?.pro || 0}, HUB ×{selectedClient.existing_devices?.hub || 0}, LITE ×{selectedClient.existing_devices?.lite || 0}
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Step 2: Device Selection */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Step 2 — Select devices to dispatch</CardTitle>
              <span className="text-sm text-slate-500">{selectedDevices.length} selected</span>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-slate-500 mb-3">QC-passed · in stock · not yet provisioned</p>
            
            <div className="border rounded-lg dark:border-slate-700">
              <div className="grid grid-cols-4 gap-2 p-2 border-b bg-slate-50 dark:bg-slate-800/50 dark:border-slate-700 text-xs font-medium text-slate-500">
                <span></span>
                <span>Device</span>
                <span>Type</span>
                <span>Status</span>
              </div>
              
              {availableDevices.map((device) => (
                <div
                  key={device.id}
                  className={`grid grid-cols-4 gap-2 p-2 items-center border-b last:border-b-0 dark:border-slate-700 ${
                    !device.selectable ? 'opacity-40' : 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50'
                  }`}
                  onClick={() => toggleDevice(device.id)}
                >
                  <div className="flex justify-center">
                    <div
                      className={`w-4 h-4 rounded border flex items-center justify-center ${
                        selectedDevices.includes(device.id)
                          ? 'bg-blue-600 border-blue-600'
                          : device.selectable
                          ? 'border-slate-300 dark:border-slate-600'
                          : 'border-slate-200 dark:border-slate-700'
                      }`}
                    >
                      {selectedDevices.includes(device.id) && (
                        <Check className="w-3 h-3 text-white" />
                      )}
                    </div>
                  </div>
                  <span className="font-mono text-sm">{device.device_id}</span>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium w-fit ${getTypeColor(device.type)}`}>
                    {device.type}
                  </span>
                  <Badge
                    className={
                      device.qc_status === 'passed'
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                        : device.qc_status === 'fota'
                        ? 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400'
                        : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                    }
                  >
                    {device.qc_status === 'passed' ? 'QC pass' : device.qc_status === 'fota' ? 'FOTA in progress' : 'QC failed'}
                  </Badge>
                </div>
              ))}
            </div>
            
            <p className="text-xs text-slate-400 mt-3">
              Greyed devices are unavailable — FOTA in progress or QC failed. Cannot be dispatched until QC passes.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Step 3 & 4 */}
      <div className="grid grid-cols-2 gap-6">
        {/* Step 3: Confirm Summary */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Step 3 — Confirm dispatch summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Client</span>
                <span className="font-medium">{selectedClient?.name || '—'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">PRO devices</span>
                <span className="font-medium">{summary.pro} units</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">HUB devices</span>
                <span className="font-medium">{summary.hub} units</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">LITE devices</span>
                <span className="font-medium">{summary.lite} units</span>
              </div>
              <div className="flex justify-between text-sm pt-2 border-t dark:border-slate-700">
                <span className="text-slate-500">Total dispatched</span>
                <span className="font-bold">{summary.total} devices</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Status after confirm</span>
                <span className="text-green-600 font-medium">PROVISIONED → {(selectedClient?.name || 'client').split(' · ')[0]} pool</span>
              </div>
            </div>

            <div>
              <label className="text-sm text-slate-500 mb-1 block">Delivery note (optional)</label>
              <Textarea
                value={deliveryNote}
                onChange={(e) => setDeliveryNote(e.target.value)}
                placeholder="Add any notes about this dispatch..."
                className="h-20"
              />
            </div>

            <Button
              className="w-full bg-blue-600 hover:bg-blue-700"
              disabled={!selectedClient || selectedDevices.length === 0}
              onClick={() => setConfirmModalOpen(true)}
            >
              Confirm dispatch · provision to {(selectedClient?.name || 'client').split(' · ')[0]}
            </Button>
          </CardContent>
        </Card>

        {/* Step 4: Client Notification */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Step 4 — Client notification</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-500 mb-4">On confirmation, client receives:</p>

            <div className="space-y-4">
              {[
                {
                  letter: 'A',
                  title: 'Email to Tenant Admin',
                  desc: 'Device serial numbers, types, and count. Login link to client portal. "Your devices are ready to assign to agents."'
                },
                {
                  letter: 'B',
                  title: 'Devices appear in client portal',
                  desc: 'Status: PROVISIONED. Client can see them under Devices → My pool. Status will update to ACTIVE when agent scans the device QR to start a trip.'
                },
                {
                  letter: 'C',
                  title: 'Device state machine transitions',
                  desc: 'IN_STOCK → PROVISIONED (on this action). PROVISIONED → ACTIVE (when agent scans to start trip). ACTIVE → RETURNED (agent end-of-day scan).'
                }
              ].map((item) => (
                <div key={item.letter} className="flex gap-3">
                  <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 flex items-center justify-center text-xs font-medium flex-shrink-0">
                    {item.letter}
                  </div>
                  <div>
                    <p className="text-sm font-medium">{item.title}</p>
                    <p className="text-xs text-slate-500 mt-1 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Dispatch History */}
      <div>
        <h3 className="text-base font-semibold mb-3">Dispatch history</h3>
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[100px]">Date</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead className="w-[60px] text-center">PRO</TableHead>
                  <TableHead className="w-[60px] text-center">HUB</TableHead>
                  <TableHead className="w-[60px] text-center">LITE</TableHead>
                  <TableHead className="w-[100px]">By</TableHead>
                  <TableHead className="w-[100px]">Status</TableHead>
                  <TableHead className="w-[60px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dispatchHistory.map((dispatch, i) => (
                  <TableRow key={`idx-${i}`}>
                    <TableCell className="text-sm">{dispatch.date}</TableCell>
                    <TableCell>{dispatch.client}</TableCell>
                    <TableCell className="text-center">{dispatch.pro}</TableCell>
                    <TableCell className="text-center">{dispatch.hub}</TableCell>
                    <TableCell className="text-center">{dispatch.lite}</TableCell>
                    <TableCell className="text-sm text-slate-500">{dispatch.by}</TableCell>
                    <TableCell>
                      <Badge
                        className={
                          dispatch.status === 'delivered'
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                            : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                        }
                      >
                        {dispatch.status === 'delivered' ? 'Delivered' : 'In transit'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" className="text-blue-600">
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* Confirmation Modal */}
      <Dialog open={confirmModalOpen} onOpenChange={setConfirmModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Device Dispatch</DialogTitle>
            <DialogDescription>
              You are about to dispatch {summary.total} devices to {selectedClient?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span>PRO devices</span>
              <span className="font-medium">{summary.pro}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span>HUB devices</span>
              <span className="font-medium">{summary.hub}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span>LITE devices</span>
              <span className="font-medium">{summary.lite}</span>
            </div>
            <div className="flex justify-between text-sm font-bold pt-2 border-t dark:border-slate-700">
              <span>Total</span>
              <span>{summary.total} devices</span>
            </div>
          </div>

          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Mail className="w-4 h-4" />
            <span>Client admin will receive an email notification</span>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmModalOpen(false)}>
              Cancel
            </Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700"
              onClick={handleConfirmDispatch}
              disabled={confirming}
            >
              {confirming ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Dispatching...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Confirm Dispatch
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
